/**
 * What the upload screens say, in one place.
 *
 * Every screen that reports an upload problem — the progress card, the result
 * card, the desktop review list, the banner — used to carry its own sentence,
 * and several of them stated a cause as fact that we have never measured:
 * "still in Google Photos". The browser's own error only says it lost access to
 * a file ("typically due to permission problems"). It does not say why, and the
 * logs show the same failure on photos that were demonstrably on the phone. So
 * the copy here names what we know (your phone stopped sharing the photo) and
 * offers the causes as possibilities.
 *
 * Voice rules (Picaggo_Common/voice/VOICE.md): the person's point of view, the
 * cause and the next move in every error, no "failed", no "please try again".
 * Keys for the catalog live in Picaggo_Common/content/strings.json under
 * `upload.*` — keep the two in step.
 */
import type { UploadFailureKind } from './upload-error.util';

/** Same set the dialog groups failures by, plus its own validation verdict. */
export type FailureKindForCopy = UploadFailureKind | 'invalid-file';

export type CopyDevice = 'phone' | 'computer';

/** What the event is called: the host's "album", a guest's "Event Gallery". */
export function albumWord(isHost: boolean): string {
  return isHost ? 'album' : 'Event Gallery';
}

const photos = (n: number): string => (n === 1 ? '1 photo' : `${n} photos`);
const itThem = (n: number): string => (n === 1 ? 'it' : 'them');

/**
 * One sentence of cause plus the next move, for a group of `n` photos that
 * ended the same way. Used wherever failures are summarised.
 */
export function failureGroupMessage(kind: FailureKindForCopy, n: number, device: CopyDevice, isHost: boolean): string {
  switch (kind) {
    case 'file-unreadable':
      return `${photos(n)} couldn't be read from your ${device}. Add ${itThem(n)} again — if that doesn't work, download ${itThem(n)} from Google Photos first.`;
    case 'offline':
      return `Your connection dropped, so ${photos(n)} didn't upload. Reconnect, then tap Retry.`;
    case 'aws-unreachable':
      return `Your connection was too weak to reach Picaggo, so ${photos(n)} didn't upload. Try Wi-Fi or a stronger signal, then retry.`;
    case 'timeout':
      return `${photos(n)} took too long to send on this connection. Try Wi-Fi or a stronger signal, then retry.`;
    case 'throttled':
    case 'server-error':
      return `Picaggo was too busy to take ${photos(n)} just now. Wait a minute, then retry.`;
    case 'credentials':
      return `We couldn't confirm your sign-in, so ${photos(n)} didn't upload. Reopen Picaggo, then retry.`;
    case 'request-blocked':
      return `Something on your connection blocked ${photos(n)} before they reached Picaggo. A VPN, firewall or browser extension can do this — turn it off, then retry.`;
    case 'access-denied':
    case 'not-found':
      return `Picaggo couldn't accept ${photos(n)}. Add ${itThem(n)} again. If it keeps happening, reach out to us.`;
    case 'verification':
      return `${photos(n)} reached Picaggo, but we couldn't confirm ${n === 1 ? 'it arrived' : 'they arrived'} whole. Check the ${albumWord(isHost)} and add any that are missing.`;
    case 'aborted':
      return `${photos(n)} ${n === 1 ? 'was' : 'were'} cancelled before finishing. Add ${itThem(n)} again when you're ready.`;
    case 'invalid-file':
      return `${photos(n)} can't be uploaded as ${n === 1 ? 'it is' : 'they are'} — empty, damaged or too big. Pick ${n === 1 ? 'a different one' : 'different ones'}.`;
    default:
      return `${photos(n)} didn't upload and we're not sure why. Try again on Wi-Fi. If it keeps happening, reach out to us.`;
  }
}

/** A few words for one file in a list — the reason, not the error text. */
export function failureReason(kind: FailureKindForCopy, device: CopyDevice): string {
  switch (kind) {
    case 'file-unreadable':
      return `Couldn't be read from your ${device}`;
    case 'offline':
      return 'Your connection dropped';
    case 'aws-unreachable':
      return 'Connection too weak to reach Picaggo';
    case 'timeout':
      return 'Took too long to send';
    case 'throttled':
    case 'server-error':
      return 'Picaggo was busy';
    case 'credentials':
      return "Sign-in couldn't be confirmed";
    case 'request-blocked':
      return 'Blocked before reaching Picaggo';
    case 'access-denied':
    case 'not-found':
      return "Picaggo couldn't accept it";
    case 'verification':
      return "Couldn't confirm it arrived whole";
    case 'aborted':
      return 'Cancelled';
    case 'invalid-file':
      return 'Empty, damaged or too big';
    default:
      return "Didn't upload";
  }
}

// --- Photos the browser would not read ------------------------------------

/** Seen under the progress bar the moment the first one turns up. */
export function unreadableInProgress(n: number, device: CopyDevice): string {
  return `${photos(n)} couldn't be read from your ${device}. We'll upload the rest — then you can add ${itThem(n)} again.`;
}

/** Tag on a row of the desktop file list for a file the pick-time check couldn't read. */
export const UNREADABLE_TAG = "Can't be read";

/** Title of the panel shown at pick time, before anything uploads. */
export function unreadablePickTitle(n: number, device: CopyDevice): string {
  return `${photos(n)} can't be read from your ${device} right now`;
}

/** Body of that panel: depends on whether anything else is ready to go. */
export function unreadablePickBody(n: number, readyCount: number): string {
  return readyCount > 0
    ? `Upload the rest now. We'll show you what to try for ${n === 1 ? 'this one' : 'these'} after.`
    : `Add ${itThem(n)} again. If that doesn't work, download ${itThem(n)} from Google Photos first.`;
}

/** Title of the panel on the result card, after the rest have uploaded. */
export function unreadableResultTitle(n: number): string {
  return `${n === 1 ? "1 photo wasn't" : `${n} photos weren't`} added. Your phone wouldn't share ${itThem(n)} with Picaggo.`;
}

/** Subtitle of the result card when every miss is an unreadable photo. */
export function unreadableResultSubtitle(n: number): string {
  return `Your phone wouldn't let us read the other ${n}.`;
}

/** Toast after "Try again" on the pick-time panel found nothing new. */
export function unreadableStillBlocked(n: number): string {
  return n === 1 ? "Your phone still won't share it" : "Your phone still won't share them";
}

// --- The connection, while an upload is running ---------------------------

export function slowConnectionNotice(speedKBps: number): string {
  const speed = speedKBps >= 1 ? `about ${Math.round(speedKBps)} KB/s` : 'very slow';
  return `Your connection is slow right now (${speed}), so this will take a while. Wi-Fi or a stronger signal will help. Keep Picaggo open.`;
}

export function stalledConnectionNotice(): string {
  return "Your connection has stopped responding. We'll keep trying while Picaggo stays open.";
}

export function waitingForConnectionNotice(): string {
  return "Waiting for your connection to come back. We'll carry on from where we left off.";
}

// --- Leaving the page ------------------------------------------------------

/** Before an upload starts: the promise we can actually keep. */
export const KEEP_PICAGGO_OPEN_HINT =
  "You can browse around Picaggo while this uploads. Keep the app open — switching to another app or tab can stop it.";

export const BANNER_KEEP_OPEN = 'keep Picaggo open';

export function bannerStopped(remaining: number): string {
  return remaining === 1
    ? "Uploading stopped — Picaggo was closed or paused. 1 photo didn't finish."
    : `Uploading stopped — Picaggo was closed or paused. ${remaining} photos didn't finish.`;
}

export const BANNER_STOPPED_ACTION = 'Tap to add them again';

/** Banner once a batch is finished. It stays until the person closes it. */
export function bannerDoneTitle(failed: number, stopped = 0): string {
  if (stopped > 0) {
    return UPLOAD_STOPPED_TITLE;
  }
  return failed > 0 ? 'Upload finished' : 'Your photos are in';
}

/** `failed` includes the `stopped` photos, as the dialog counts them. */
export function bannerDoneSubtitle(added: number, total: number, failed: number, eventName: string, stopped = 0): string {
  if (stopped > 0) {
    const otherMisses = Math.max(0, failed - stopped);
    return `${added} of ${total} added to ${eventName} · ${stopped} not sent`
      + (otherMisses > 0 ? ` · ${otherMisses} didn't upload` : '')
      + ' · Tap to view';
  }
  if (failed > 0) {
    return `${added} of ${total} added to ${eventName} · ${failed} didn't upload · Tap to review`;
  }
  return `${added} added to ${eventName} · Tap to view`;
}

/** Reopened dialog while files are still on their way (not yet processing). */
export function transferTitleFor(isHost: boolean): string {
  return `Uploading to your ${albumWord(isHost)}`;
}
export const TRANSFER_NOTE = 'Keep Picaggo open until every photo is uploaded.';
export function transferCount(uploaded: number, total: number): string {
  return `${uploaded} of ${total} uploaded`;
}

// --- Closing the dialog while photos are still being sent -------------------

/**
 * The minimize action during the transfer. Browsing Picaggo is what it allows;
 * "continue in background" read as permission to leave the app, which is what
 * ends an upload.
 */
export const BROWSE_WHILE_UPLOADING = 'Browse Picaggo while it uploads';

/** ✕ while photos are still on their way asks this instead of closing. */
export function stopUploadTitle(remaining: number): string {
  return `Stop uploading ${photos(remaining)}?`;
}

/** Under that question: what stopping keeps, and what finishing needs. */
export function stopUploadBody(device: CopyDevice, isHost: boolean): string {
  const kept = `Photos already uploaded stay in the ${albumWord(isHost)}.`;
  return device === 'phone'
    ? `${kept} To finish the rest, keep Picaggo open — switching to another app or locking your phone can stop it.`
    : `${kept} To finish the rest, keep this tab open.`;
}

export const STOP_UPLOAD_KEEP = 'Keep uploading';
export const STOP_UPLOAD_CONFIRM = 'Stop uploading';

/** After a stop, while the photos already on their way finish. */
export const STOPPING_TITLE = 'Stopping the upload';
export const STOPPING_NOTE = 'Finishing the photos already on their way.';

/** An upload started while another one in this window is still sending. */
export const UPLOAD_QUEUED_TITLE = 'Waiting for your other upload';
export const UPLOAD_QUEUED_NOTE = "These start as soon as it finishes. Keep Picaggo open.";

/** Result title whenever the person stopped the upload. */
export const UPLOAD_STOPPED_TITLE = 'Upload stopped';

/**
 * The photos a stop left out, said as what the person did. They used to share
 * the 'aborted' wording ("cancelled before finishing") and sit under "Failed
 * Uploads", which read as a fault on the very screen that follows a deliberate stop.
 */
export function stoppedGroupMessage(n: number): string {
  return `${photos(n)} ${n === 1 ? "wasn't" : "weren't"} sent because you stopped the upload. Add ${itThem(n)} again when you're ready.`;
}

/** One left-out photo in the desktop list. */
export const STOPPED_REASON = 'Not sent: you stopped the upload';

/** Heading of the desktop list when every miss was left out by the stop. */
export const STOPPED_SECTION_TITLE = 'Not sent';
